import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import FormFunnelAnalytics from '../components/FormFunnelAnalytics';
import { apiRequest } from '../config/api';
import SkeletonFormAnalytics from '../components/skeletons/SkeletonFormAnalytics';

export default function FormAnalytics() {
  const { id } = useParams(); // Changed from form_id to id to match route
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const fetchForm = useCallback(async () => {
    try {
      const response = await apiRequest(`/forms/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch form');
      }
      const data = await response.json();
      setForm(data);
    } catch (err) {
      console.error('Error fetching form:', err);
    }
  }, [id]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/analytics/forms/${id}/summary?days=${days}`);
      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }
      const data = await response.json();
      setAnalytics(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [id, days]);

  useEffect(() => {
    fetchForm();
    fetchAnalytics();
  }, [fetchForm, fetchAnalytics]);

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value) => {
    return `${value}%`;
  };

  if (loading) return <SkeletonFormAnalytics />;

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600 text-lg">Error: {error}</div>
        <button 
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-8 text-center">
        <div className="text-gray-600">No analytics data available</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <h1 className="text-3xl font-bold text-gray-900">
            {form ? `${form.title} - Analytics` : 'Form Analytics'}
          </h1>
          {form && form.current_version && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              v{form.current_version}
            </span>
          )}
        </div>
        
        {/* Time Range Selector */}
        <div className="flex space-x-4 mb-6">
          <select 
            value={days} 
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Total Views</div>
            <div className="text-3xl font-bold text-blue-600">{analytics.total_views}</div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Form Starts</div>
            <div className="text-3xl font-bold text-green-600">{analytics.total_starts}</div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Completions</div>
            <div className="text-3xl font-bold text-purple-600">{analytics.total_completions}</div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Completion Rate</div>
            <div className="text-3xl font-bold text-indigo-600">{formatPercentage(analytics.completion_rate)}</div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500 mb-2">Average Session Duration</div>
            <div className="text-xl font-bold text-gray-900">
              {formatDuration(analytics.average_session_duration)}
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500 mb-2">Conversion Funnel</div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Views → Starts:</span>
                <span className="font-semibold">
                  {analytics.total_views > 0 ? 
                    formatPercentage(((analytics.total_starts / analytics.total_views) * 100).toFixed(1)) 
                    : '0%'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span>Starts → Completions:</span>
                <span className="font-semibold">{formatPercentage(analytics.completion_rate)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step Analytics */}
        {analytics.step_analytics && analytics.step_analytics.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Step-by-Step Analytics</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Step
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Starts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Drop-off Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completion Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.step_analytics.map((step, index) => {
                    const completionRate = step.starts > 0 ? ((step.completions / step.starts) * 100).toFixed(1) : 0;
                    
                    return (
                      <tr key={step.step_id || index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          Step {step.step_index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {step.starts}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {step.completions}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            step.drop_off_rate > 50 ? 'bg-red-100 text-red-800' :
                            step.drop_off_rate > 25 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {formatPercentage(step.drop_off_rate)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPercentage(completionRate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Daily Analytics Chart */}
        {analytics.daily_analytics && analytics.daily_analytics.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Daily Trends</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Views
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Starts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.daily_analytics.slice(-10).map((day, index) => (
                    <tr key={day.date}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {new Date(day.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {day.views}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {day.starts}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {day.completions}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <hr className="my-6" />
        <div className="text-center"><h2 className="text-lg font-bold text-gray-800">New analytics?</h2></div>
        <FormFunnelAnalytics data={analytics} />
      </div>
    </div>
  );
}
